import React, { useCallback, useEffect, useState } from 'react';
import {
  ConsentPreferences,
  getConsent,
  setConsent,
  hasConsent,
  isGpcEnabled,
} from '@wren/shared';
import posthog from 'posthog-js';
import {
  initAnalyticsScripts,
  initMarketingScripts,
} from '../../lib/consentedScripts';
import getStore from '../../store';
import { Button } from '../buttons/ButtonNext';
import TextButton from '../buttons/TextButton';
import { colors } from '../../style/theme';

type ConsentView = 'hidden' | 'collapsed' | 'expanded' | 'saved' | 'modal';

function initFromConsent() {
  if (hasConsent('analytics')) initAnalyticsScripts();
  if (hasConsent('marketing')) initMarketingScripts();
}

function consentSummary(p: ConsentPreferences): string {
  if (p.analytics && p.marketing) return 'Saved. All cookies on.';
  if (!p.analytics && !p.marketing) return 'Saved. Necessary cookies only.';
  return `Saved. Analytics ${p.analytics ? 'on' : 'off'}, marketing ${p.marketing ? 'on' : 'off'}.`;
}

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex items-center h-6 w-11 shrink-0 cursor-pointer rounded-full border-0 p-0.5 transition-colors duration-200 ${
        checked ? 'bg-wren-green' : 'bg-gray-8'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-200 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function CollapsedBanner({
  onAccept,
  onReject,
  onChoose,
}: {
  onAccept: () => void;
  onReject: () => void;
  onChoose: () => void;
}) {
  return (
    <div className="fixed bottom-0 left-0 right-0 tablet:bottom-6 tablet:left-6 tablet:right-auto z-[10001] animate-rise-in">
      <div className="bg-white rounded-t-2xl tablet:rounded-lg shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.25)] tablet:shadow-[0_10px_24px_-6px_rgba(0,0,0,0.22)] w-full tablet:w-[440px]">
        {/* Mobile: bottom sheet */}
        <div className="tablet:hidden p-5 pb-6 flex flex-col gap-3">
          <h3 className="font-serif text-xl font-semibold leading-snug">
            Cookie preferences
          </h3>
          <p className="text-sm leading-relaxed text-gray-4">
            Necessary cookies are required to run wren.co. Analytics and
            marketing cookies are optional.
          </p>
          <div className="flex flex-col gap-2 mt-1">
            <Button
              buttonType="filled"
              shape="rounded rectangle"
              color={colors.wrenGreen}
              onClick={onAccept}
              className="w-full"
            >
              Accept all
            </Button>
            <Button
              buttonType="outlined"
              shape="rounded rectangle"
              onClick={onReject}
              className="w-full"
            >
              Reject all
            </Button>
          </div>
          <div className="flex items-center justify-center gap-5 pt-3 text-[13px]">
            <TextButton onClick={onChoose}>Choose cookies</TextButton>
            <a href="/privacy-policy" className="text-link-blue">
              Privacy policy
            </a>
          </div>
        </div>

        {/* Desktop: card */}
        <div className="hidden tablet:flex flex-col gap-3.5 p-6 pb-5">
          <h3 className="font-serif text-xl font-semibold leading-snug">
            Cookie preferences
          </h3>
          <p className="text-sm leading-relaxed text-gray-4 [text-wrap:pretty]">
            Necessary cookies are required to run wren.co. Analytics and
            marketing cookies are optional, and we only set them if you accept.
          </p>
          <div className="flex gap-2.5 mt-0.5">
            <Button
              buttonType="filled"
              shape="rounded rectangle"
              padding="tight"
              color={colors.wrenGreen}
              onClick={onAccept}
            >
              Accept all
            </Button>
            <Button
              buttonType="outlined"
              shape="rounded rectangle"
              padding="tight"
              onClick={onReject}
            >
              Reject all
            </Button>
          </div>
          <div className="flex items-center gap-4 pt-2.5 border-t border-gray-9 text-[13px]">
            <TextButton onClick={onChoose}>Choose cookies</TextButton>
            <a href="/privacy-policy" className="text-link-blue">
              Privacy policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExpandedBanner({
  prefs,
  onToggle,
  onSave,
  onReject,
  onBack,
  onOpenFullList,
}: {
  prefs: ConsentPreferences;
  onToggle: (category: 'analytics' | 'marketing') => void;
  onSave: () => void;
  onReject: () => void;
  onBack: () => void;
  onOpenFullList: () => void;
}) {
  return (
    <div className="fixed bottom-0 left-0 right-0 tablet:bottom-6 tablet:left-6 tablet:right-auto z-[10001] animate-rise-in">
      <div className="bg-white rounded-t-2xl tablet:rounded-lg shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.25)] tablet:shadow-[0_10px_24px_-6px_rgba(0,0,0,0.22)] w-full tablet:w-[560px]">
        {/* Mobile: bottom sheet */}
        <div className="tablet:hidden p-5 pb-6 flex flex-col gap-3.5">
          <div className="flex items-baseline justify-between">
            <h3 className="font-serif text-xl font-semibold">
              Choose your cookies
            </h3>
            <TextButton onClick={onBack}>Back</TextButton>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center justify-between gap-4 py-3 border-t border-gray-9">
              <span className="text-[15px] font-semibold">Necessary</span>
              <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-wren-green bg-wren-green-low px-2.5 py-1.5 rounded-full">
                Always on
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 py-3 border-t border-gray-9">
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold">Analytics</span>
                <span className="text-[13px] leading-snug text-gray-4">
                  Measures how the site is used.
                </span>
              </div>
              <ToggleSwitch
                checked={prefs.analytics}
                onChange={() => onToggle('analytics')}
              />
            </div>
            <div className="flex items-center justify-between gap-4 py-3 border-t border-b border-gray-9">
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold">Marketing</span>
                <span className="text-[13px] leading-snug text-gray-4">
                  Measures referrals and campaigns.
                </span>
              </div>
              <ToggleSwitch
                checked={prefs.marketing}
                onChange={() => onToggle('marketing')}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              buttonType="filled"
              shape="rounded rectangle"
              color={colors.wrenGreen}
              onClick={onSave}
              className="w-full"
            >
              Save choices
            </Button>
            <Button
              buttonType="outlined"
              shape="rounded rectangle"
              onClick={onReject}
              className="w-full"
            >
              Reject all
            </Button>
          </div>
        </div>

        {/* Desktop: card */}
        <div className="hidden tablet:flex flex-col gap-4 p-6">
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="font-serif text-xl font-semibold">
              Choose your cookies
            </h3>
            <TextButton onClick={onBack}>Back</TextButton>
          </div>
          <div className="flex flex-col">
            <div className="flex items-start justify-between gap-5 py-3.5 border-t border-gray-9">
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold">Necessary</span>
                <span className="text-[13px] leading-relaxed text-gray-4">
                  Required for login, checkout, and remembering this choice.
                </span>
              </div>
              <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-wren-green bg-wren-green-low px-2.5 py-1.5 rounded-full">
                Always on
              </span>
            </div>
            <div className="flex items-start justify-between gap-5 py-3.5 border-t border-gray-9">
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold">Analytics</span>
                <span className="text-[13px] leading-relaxed text-gray-4">
                  Measures how the site is used. PostHog, Plausible, Sentry.
                </span>
              </div>
              <div className="shrink-0 pt-0.5">
                <ToggleSwitch
                  checked={prefs.analytics}
                  onChange={() => onToggle('analytics')}
                />
              </div>
            </div>
            <div className="flex items-start justify-between gap-5 py-3.5 border-t border-b border-gray-9">
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold">Marketing</span>
                <span className="text-[13px] leading-relaxed text-gray-4">
                  Measures referrals and campaign performance. Awin, Trustpilot.
                </span>
              </div>
              <div className="shrink-0 pt-0.5">
                <ToggleSwitch
                  checked={prefs.marketing}
                  onChange={() => onToggle('marketing')}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <TextButton onClick={onOpenFullList}>
              Read the full cookie list
            </TextButton>
            <div className="flex gap-2.5">
              <Button
                buttonType="outlined"
                shape="rounded rectangle"
                padding="tight"
                onClick={onReject}
              >
                Reject all
              </Button>
              <Button
                buttonType="filled"
                shape="rounded rectangle"
                padding="tight"
                color={colors.wrenGreen}
                onClick={onSave}
              >
                Save choices
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SavedBanner({
  summary,
  onChange,
  onDismiss,
}: {
  summary: string;
  onChange: () => void;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-4 left-4 right-4 tablet:bottom-6 tablet:left-6 tablet:right-auto z-[10001] animate-rise-in">
      <div className="flex items-center gap-3 tablet:gap-3.5 bg-white rounded-full shadow-[0_4px_16px_-4px_rgba(0,0,0,0.2)] px-4 py-2.5 tablet:px-[18px] tablet:py-2.5">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-success-green-low text-success-green text-xs font-bold">
          ✓
        </span>
        <span className="flex-1 text-[13px] tablet:text-sm">{summary}</span>
        <TextButton onClick={onChange}>Change</TextButton>
      </div>
    </div>
  );
}

const NECESSARY_COOKIES = [
  {
    name: 'wren_session',
    purpose: 'Keeps you signed in',
    duration: 'Session',
  },
  {
    name: 'cookie_consent',
    purpose: 'Remembers the choices on this screen',
    duration: '365 days',
  },
];

const ANALYTICS_PROVIDERS = [
  {
    name: 'PostHog',
    purpose: 'Product analytics and funnel steps',
    duration: '365 days',
  },
  {
    name: 'Plausible',
    purpose: 'Page views, no personal identifiers',
    duration: 'Session',
  },
  {
    name: 'Sentry',
    purpose: 'Error and crash reports',
    duration: 'Session',
  },
];

const MARKETING_PROVIDERS = [
  {
    name: 'Awin',
    purpose: 'Credits partners who referred you',
    duration: '30 days',
  },
  {
    name: 'Trustpilot',
    purpose: 'Loads the reviews widget',
    duration: 'Session',
  },
];

function DetailTable({
  headerLabel,
  items,
}: {
  headerLabel: string;
  items: { name: string; purpose: string; duration: string }[];
}) {
  return (
    <div className="mt-3.5 border border-gray-9 rounded-md overflow-hidden animate-fade-in">
      <div className="grid grid-cols-[150px_1fr_92px] gap-3 px-3.5 py-2 bg-bg-gray text-xs font-semibold uppercase tracking-wide text-gray-5">
        <span>{headerLabel}</span>
        <span>{headerLabel === 'Cookie' ? 'Purpose' : 'What it does'}</span>
        <span>Duration</span>
      </div>
      {items.map((item) => (
        <div
          key={item.name}
          className="grid grid-cols-[150px_1fr_92px] gap-3 px-3.5 py-2.5 border-t border-gray-9 text-[13px] leading-snug"
        >
          <span className="font-mono">{item.name}</span>
          <span className="text-gray-4">{item.purpose}</span>
          <span className="text-gray-4">{item.duration}</span>
        </div>
      ))}
    </div>
  );
}

function PreferencesModal({
  prefs,
  onToggle,
  onAccept,
  onReject,
  onSave,
  onClose,
}: {
  prefs: ConsentPreferences;
  onToggle: (category: 'analytics' | 'marketing') => void;
  onAccept: () => void;
  onReject: () => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const [openSection, setOpenSection] = useState<
    'necessary' | 'analytics' | 'marketing' | null
  >('analytics');

  const toggleSection = (section: 'necessary' | 'analytics' | 'marketing') =>
    setOpenSection((prev) => (prev === section ? null : section));

  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 animate-fade-in">
      <div
        className="absolute inset-0 bg-[rgba(7,47,16,0.45)]"
        onClick={onClose}
      />
      <div className="relative w-full max-w-[640px] max-h-[90vh] bg-white rounded-lg shadow-[0_16px_40px_-12px_rgba(0,0,0,0.4)] flex flex-col overflow-hidden animate-rise-in">
        <div className="flex items-start justify-between gap-5 p-6 pb-4">
          <div className="flex flex-col gap-2">
            <h3 className="font-serif text-2xl font-semibold leading-snug">
              Cookie preferences
            </h3>
            <p className="text-sm leading-relaxed text-gray-4 max-w-[470px]">
              Choose which cookies wren.co can set. Necessary cookies can't be
              switched off. Everything else is off until you turn it on, and you
              can change this any time.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-xl leading-none text-gray-5 hover:text-gray-2 transition-colors p-1"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          {/* Necessary */}
          <div className="border-t border-gray-9 py-4">
            <div className="flex items-start justify-between gap-5">
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => toggleSection('necessary')}
                  className="appearance-none bg-transparent border-0 p-0 cursor-pointer text-base font-semibold text-left"
                >
                  Necessary
                </button>
                <span className="text-[13px] leading-relaxed text-gray-4">
                  Keeps you logged in, remembers your cart, and stores this
                  choice. Without these the site can't work.
                </span>
                <Button
                  buttonType="text"
                  size="small"
                  padding="none"
                  color={colors.linkBlue}
                  onClick={() => toggleSection('necessary')}
                  className="self-start"
                >
                  {openSection === 'necessary'
                    ? 'Hide the 2 cookies'
                    : 'Show the 2 cookies'}
                </Button>
              </div>
              <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-wren-green bg-wren-green-low px-2.5 py-1.5 rounded-full">
                Always on
              </span>
            </div>
            {openSection === 'necessary' && (
              <DetailTable headerLabel="Cookie" items={NECESSARY_COOKIES} />
            )}
          </div>

          {/* Analytics */}
          <div className="border-t border-gray-9 py-4">
            <div className="flex items-start justify-between gap-5">
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => toggleSection('analytics')}
                  className="appearance-none bg-transparent border-0 p-0 cursor-pointer text-base font-semibold text-left"
                >
                  Analytics
                </button>
                <span className="text-[13px] leading-relaxed text-gray-4">
                  Measures how the site is used — pages visited, where people
                  stop, which errors they hit. Aggregated, never sold.
                </span>
                <Button
                  buttonType="text"
                  size="small"
                  padding="none"
                  color={colors.linkBlue}
                  onClick={() => toggleSection('analytics')}
                  className="self-start"
                >
                  {openSection === 'analytics'
                    ? 'Hide the 3 providers'
                    : 'Show the 3 providers'}
                </Button>
              </div>
              <div className="shrink-0 pt-0.5">
                <ToggleSwitch
                  checked={prefs.analytics}
                  onChange={() => onToggle('analytics')}
                />
              </div>
            </div>
            {openSection === 'analytics' && (
              <DetailTable headerLabel="Provider" items={ANALYTICS_PROVIDERS} />
            )}
          </div>

          {/* Marketing */}
          <div className="border-t border-b border-gray-9 py-4">
            <div className="flex items-start justify-between gap-5">
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => toggleSection('marketing')}
                  className="appearance-none bg-transparent border-0 p-0 cursor-pointer text-base font-semibold text-left"
                >
                  Marketing
                </button>
                <span className="text-[13px] leading-relaxed text-gray-4">
                  Measures referrals and campaign performance, and loads our
                  reviews widget. Set by third parties on our behalf.
                </span>
                <Button
                  buttonType="text"
                  size="small"
                  padding="none"
                  color={colors.linkBlue}
                  onClick={() => toggleSection('marketing')}
                  className="self-start"
                >
                  {openSection === 'marketing'
                    ? 'Hide the 2 providers'
                    : 'Show the 2 providers'}
                </Button>
              </div>
              <div className="shrink-0 pt-0.5">
                <ToggleSwitch
                  checked={prefs.marketing}
                  onChange={() => onToggle('marketing')}
                />
              </div>
            </div>
            {openSection === 'marketing' && (
              <DetailTable headerLabel="Provider" items={MARKETING_PROVIDERS} />
            )}
          </div>

          <p className="my-4 text-[13px] leading-relaxed text-gray-5">
            Full detail is in our{' '}
            <a
              href="/privacy-policy"
              className="text-link-blue hover:text-link-blue-hover"
            >
              privacy policy
            </a>
            . To withdraw consent later, use "Cookie settings" in the footer.
          </p>
        </div>

        <div className="relative px-6 py-4 bg-white">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-8 to-transparent" />
          <div className="flex items-center justify-between gap-3">
            <Button
              buttonType="outlined"
              shape="rounded rectangle"
              padding="tight"
              onClick={onReject}
            >
              Reject all
            </Button>
            <div className="flex gap-2.5">
              <Button
                buttonType="outlined"
                shape="rounded rectangle"
                padding="tight"
                onClick={onAccept}
              >
                Accept all
              </Button>
              <Button
                buttonType="filled"
                shape="rounded rectangle"
                padding="tight"
                color={colors.wrenGreen}
                onClick={onSave}
              >
                Save choices
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConsentBanner() {
  const [view, setView] = useState<ConsentView>('hidden');
  const [prefs, setPrefs] = useState<ConsentPreferences>({
    analytics: false,
    marketing: false,
  });

  const identifyIfNeeded = useCallback(() => {
    try {
      const store = getStore();
      const userId = store?.getState()?.user?.userId;
      if (userId && !posthog._isIdentified()) {
        posthog.identify(userId as unknown as string);
      }
    } catch {
      // No Redux store in Next.js — skip PostHog identify
    }
  }, []);

  useEffect(() => {
    if (isGpcEnabled()) {
      const existing = getConsent();
      if (!existing) {
        setConsent({ analytics: false, marketing: false });
      }
      return;
    }

    const existing = getConsent();
    if (existing) {
      setPrefs(existing);
      initFromConsent();
      if (hasConsent('analytics')) identifyIfNeeded();
    } else {
      setView('collapsed');
    }
  }, [identifyIfNeeded]);

  useEffect(() => {
    const handler = () => {
      const existing = getConsent();
      if (existing) setPrefs(existing);
      setView('modal');
    };
    window.addEventListener('open-cookie-settings', handler);
    return () => window.removeEventListener('open-cookie-settings', handler);
  }, []);

  const togglePref = useCallback((category: 'analytics' | 'marketing') => {
    setPrefs((p) => ({ ...p, [category]: !p[category] }));
  }, []);

  const handleAcceptAll = useCallback(() => {
    const newPrefs = { analytics: true, marketing: true };
    setConsent(newPrefs);
    setPrefs(newPrefs);
    setView('saved');
    initAnalyticsScripts();
    identifyIfNeeded();
    initMarketingScripts();
  }, [identifyIfNeeded]);

  const handleRejectAll = useCallback(() => {
    const newPrefs = { analytics: false, marketing: false };
    setConsent(newPrefs);
    setPrefs(newPrefs);
    setView('saved');
  }, []);

  const handleSave = useCallback(() => {
    setConsent(prefs);
    setView('saved');
    if (prefs.analytics) {
      initAnalyticsScripts();
      identifyIfNeeded();
    }
    if (prefs.marketing) initMarketingScripts();
  }, [prefs, identifyIfNeeded]);

  return (
    <>
      {view === 'collapsed' && (
        <CollapsedBanner
          onAccept={handleAcceptAll}
          onReject={handleRejectAll}
          onChoose={() => setView('expanded')}
        />
      )}
      {view === 'expanded' && (
        <ExpandedBanner
          prefs={prefs}
          onToggle={togglePref}
          onSave={handleSave}
          onReject={handleRejectAll}
          onBack={() => setView('collapsed')}
          onOpenFullList={() => setView('modal')}
        />
      )}
      {view === 'saved' && (
        <SavedBanner
          summary={consentSummary(prefs)}
          onChange={() => setView('expanded')}
          onDismiss={() => setView('hidden')}
        />
      )}
      {view === 'modal' && (
        <PreferencesModal
          prefs={prefs}
          onToggle={togglePref}
          onAccept={handleAcceptAll}
          onReject={handleRejectAll}
          onSave={handleSave}
          onClose={() => {
            const existing = getConsent();
            setView(existing ? 'hidden' : 'collapsed');
          }}
        />
      )}
    </>
  );
}

export function CookieSettingsLink() {
  return (
    <Button
      buttonType="text"
      padding="none"
      style={{ minHeight: 0, borderWidth: 0, lineHeight: 'inherit' }}
      onClick={() =>
        window.dispatchEvent(new CustomEvent('open-cookie-settings'))
      }
    >
      Cookie settings
    </Button>
  );
}
